class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 500);

    const facetForm = this.querySelector('form');
    facetForm.addEventListener('input', this.debouncedOnSubmit.bind(this));

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');
    FacetFiltersForm.setLocalizedTitleSortState(searchParams);
    document.getElementById('ProductGridContainer').querySelector('.collection').classList.add('loading');
    if (countContainer) {
      countContainer.classList.add('loading');
    }
    if (countContainerDesktop) {
      countContainerDesktop.classList.add('loading');
    }

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event)
        : FacetFiltersForm.renderSectionFromFetch(url, event);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static async renderSectionFromFetch(url, event) {
    const response = await fetch(url);
    const html = await response.text();
    const parsedHTML = FacetFiltersForm.parseHTML(html);
    let htmlToCache = html;

    if (FacetFiltersForm.isLocalizedTitleSort(url, FacetFiltersForm.getCurrentSortBy(parsedHTML))) {
      try {
        await FacetFiltersForm.applyLocalizedTitleSort(parsedHTML, url, html);
        htmlToCache = parsedHTML.documentElement.outerHTML;
      } catch (error) {
        console.error('Localized title sort failed', error);
      }
    }

    FacetFiltersForm.filterData.push({ html: htmlToCache, url });
    await FacetFiltersForm.renderSection(parsedHTML, event);
  }

  static async renderSectionFromCache(filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    const parsedHTML = FacetFiltersForm.parseHTML(html);
    await FacetFiltersForm.renderSection(parsedHTML, event);
  }

  static async renderSection(parsedHTML, event) {
    FacetFiltersForm.renderFilters(parsedHTML, event);
    await FacetFiltersForm.renderProductGridContainer(parsedHTML);
    FacetFiltersForm.renderProductCount(parsedHTML);

    if (typeof initializeScrollAnimationTrigger === 'function') {
      initializeScrollAnimationTrigger(document.getElementById('ProductGridContainer'));
    }
  }

  static parseHTML(html) {
    return new DOMParser().parseFromString(html, 'text/html');
  }

  static async renderProductGridContainer(parsedHTML) {
    document.getElementById('ProductGridContainer').innerHTML =
      parsedHTML.getElementById('ProductGridContainer').innerHTML;

    FacetFiltersForm.updateGridItemAnimationOrder(document.getElementById('ProductGridContainer'));

    document
      .getElementById('ProductGridContainer')
      .querySelectorAll('.scroll-trigger')
      .forEach((element) => {
        element.classList.add('scroll-trigger--cancel');
      });

    FacetFiltersForm.setLocalizedTitleSortReady();
  }

  static renderProductCount(parsedHTML) {
    const count = parsedHTML.getElementById('ProductCount').innerHTML;
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');
    container.innerHTML = count;
    container.classList.remove('loading');
    if (containerDesktop) {
      containerDesktop.innerHTML = count;
      containerDesktop.classList.remove('loading');
    }
  }

  static renderFilters(parsedHTML, event) {
    const facetDetailsElements = parsedHTML.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );
    const matchesIndex = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.dataset.index === jsFilter.dataset.index : false;
    };
    const facetsToRender = Array.from(facetDetailsElements).filter((element) => !matchesIndex(element));
    const countsToRender = Array.from(facetDetailsElements).find(matchesIndex);

    facetsToRender.forEach((element) => {
      document.querySelector(`.js-filter[data-index="${element.dataset.index}"]`).innerHTML = element.innerHTML;
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    if (countsToRender) FacetFiltersForm.renderCounts(countsToRender, event.target.closest('.js-filter'));
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      document.querySelector(selector).innerHTML = activeFacetsElement.innerHTML;
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-facets__open', '.mobile-facets__count', '.sorting'];

    mobileElementSelectors.forEach((selector) => {
      if (!html.querySelector(selector)) return;
      document.querySelector(selector).innerHTML = html.querySelector(selector).innerHTML;
    });

    document.getElementById('FacetFiltersFormMobile').closest('menu-drawer').bindEvents();
  }

  static renderCounts(source, target) {
    const targetElement = target.querySelector('.facets__selected');
    const sourceElement = source.querySelector('.facets__selected');

    const targetElementAccessibility = target.querySelector('.facets__summary');
    const sourceElementAccessibility = source.querySelector('.facets__summary');

    if (sourceElement && targetElement) {
      target.querySelector('.facets__selected').outerHTML = source.querySelector('.facets__selected').outerHTML;
    }

    if (targetElementAccessibility && sourceElementAccessibility) {
      target.querySelector('.facets__summary').outerHTML = source.querySelector('.facets__summary').outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static getSections() {
    return [
      {
        section: document.getElementById('product-grid').dataset.id,
      },
    ];
  }

  static getCurrentSortBy(parsedHTML = document) {
    const productGridContainer = parsedHTML.getElementById
      ? parsedHTML.getElementById('ProductGridContainer')
      : parsedHTML.querySelector('#ProductGridContainer');
    const datasetSortBy = productGridContainer?.dataset.currentSortBy;

    if (datasetSortBy) return datasetSortBy;

    const sortSelect = parsedHTML.querySelector ? parsedHTML.querySelector('#SortBy') : null;
    return sortSelect?.value || '';
  }

  static isLocalizedTitleSort(searchParamsSource = window.location.search.slice(1), fallbackSortBy = '') {
    const localizedRoot = window.Shopify?.routes?.root || '/';
    if (localizedRoot === '/') return false;

    let searchParams;

    if (searchParamsSource instanceof URL) {
      searchParams = searchParamsSource.searchParams;
    } else if (String(searchParamsSource).includes('?')) {
      searchParams = new URL(searchParamsSource, window.location.origin).searchParams;
    } else {
      searchParams = new URLSearchParams(String(searchParamsSource).replace(/^\?/, ''));
    }

    const sortBy = searchParams.get('sort_by') || fallbackSortBy;

    return sortBy === 'title-ascending' || sortBy === 'title-descending';
  }

  static setLocalizedTitleSortState(searchParams = window.location.search.slice(1)) {
    const container = document.getElementById('ProductGridContainer');
    if (!container) return;

    const isActive = FacetFiltersForm.isLocalizedTitleSort(searchParams, container.dataset.currentSortBy || '');
    container.dataset.localizedTitleSortActive = isActive ? 'true' : 'false';
    container.dataset.localizedTitleSortReady = isActive ? 'false' : 'true';
  }

  static setLocalizedTitleSortReady() {
    const container = document.getElementById('ProductGridContainer');
    if (!container) return;

    container.dataset.localizedTitleSortReady = 'true';
  }

  static getLocalizedTitleSortCacheKey(url) {
    const parsedUrl = new URL(url, window.location.origin);
    parsedUrl.searchParams.delete('page');
    parsedUrl.searchParams.delete('section_id');
    return `${parsedUrl.pathname}?${parsedUrl.searchParams.toString()}`;
  }

  static getLocalizedTitleSortCurrentPage(url) {
    const page = new URL(url, window.location.origin).searchParams.get('page');
    return Number(page || 1);
  }

  static getLocalizedTitleSortTotalPages(parsedHTML) {
    let totalPages = 1;

    parsedHTML.querySelectorAll('.pagination__item').forEach((element) => {
      const pageNumber = Number.parseInt(element.textContent.trim(), 10);
      if (!Number.isNaN(pageNumber)) {
        totalPages = Math.max(totalPages, pageNumber);
      }
    });

    return totalPages;
  }

  static getLocalizedTitleSortProductsPerPage(parsedHTML) {
    const container = document.getElementById('ProductGridContainer');
    const datasetValue = Number.parseInt(container?.dataset.productsPerPage || '', 10);
    if (!Number.isNaN(datasetValue) && datasetValue > 0) return datasetValue;

    return parsedHTML.querySelectorAll('#product-grid > .grid__item').length;
  }

  static getLocalizedTitleSortItems(parsedHTML) {
    return Array.from(parsedHTML.querySelectorAll('#product-grid > .grid__item')).map((item) => {
      const titleLink = item.querySelector('.card__heading a.full-unstyled-link');

      return {
        html: item.outerHTML,
        title: titleLink ? titleLink.textContent.trim() : '',
      };
    });
  }

  static sortLocalizedTitleSortItems(items, sortBy) {
    const locale = document.documentElement.lang || undefined;
    const sortedItems = [...items].sort((firstItem, secondItem) =>
      firstItem.title.localeCompare(secondItem.title, locale, {
        numeric: true,
        sensitivity: 'base',
      })
    );

    if (sortBy === 'title-descending') {
      sortedItems.reverse();
    }

    return sortedItems;
  }

  static async getLocalizedTitleSortData(url, parsedHTML, html) {
    const cacheKey = FacetFiltersForm.getLocalizedTitleSortCacheKey(url);
    if (FacetFiltersForm.localizedTitleSortData[cacheKey]) {
      return FacetFiltersForm.localizedTitleSortData[cacheKey];
    }

    const currentUrl = new URL(url, window.location.origin);
    const currentUrlString = currentUrl.toString();
    const totalPages = FacetFiltersForm.getLocalizedTitleSortTotalPages(parsedHTML);
    const productsPerPage = FacetFiltersForm.getLocalizedTitleSortProductsPerPage(parsedHTML);
    const currentSortBy = currentUrl.searchParams.get('sort_by') || FacetFiltersForm.getCurrentSortBy(parsedHTML);
    const pageUrls = Array.from({ length: totalPages }, (_, index) => {
      const pageUrl = new URL(currentUrlString);

      if (index === 0) {
        pageUrl.searchParams.delete('page');
      } else {
        pageUrl.searchParams.set('page', index + 1);
      }

      return pageUrl.toString();
    });

    const pageHtmlResponses = await Promise.all(
      pageUrls.map((pageUrl) => {
        if (pageUrl === currentUrlString) return Promise.resolve(html);
        return fetch(pageUrl).then((response) => response.text());
      })
    );

    const items = pageHtmlResponses.flatMap((pageHtml) => {
      const pageHTML = FacetFiltersForm.parseHTML(pageHtml);
      return FacetFiltersForm.getLocalizedTitleSortItems(pageHTML);
    });

    FacetFiltersForm.localizedTitleSortData[cacheKey] = {
      items: FacetFiltersForm.sortLocalizedTitleSortItems(items, currentSortBy),
      productsPerPage,
    };

    return FacetFiltersForm.localizedTitleSortData[cacheKey];
  }

  static async applyLocalizedTitleSort(parsedHTML, url, html) {
    const productGrid = parsedHTML.getElementById('product-grid');
    if (!productGrid) return;

    const currentPage = FacetFiltersForm.getLocalizedTitleSortCurrentPage(url);
    const { items, productsPerPage } = await FacetFiltersForm.getLocalizedTitleSortData(url, parsedHTML, html);
    const pageStart = (currentPage - 1) * productsPerPage;
    const pageEnd = pageStart + productsPerPage;
    const pageItems = items.slice(pageStart, pageEnd);

    productGrid.innerHTML = pageItems.map((item) => item.html).join('');
  }

  static updateGridItemAnimationOrder(container) {
    const gridItems = container?.querySelectorAll('#product-grid > .grid__item');
    if (!gridItems) return;

    gridItems.forEach((item, index) => {
      if (item.hasAttribute('data-cascade')) {
        item.style.setProperty('--animation-order', index + 1);
      }
    });
  }

  static async initializeLocalizedTitleSort() {
    const container = document.getElementById('ProductGridContainer');
    const currentSortBy = container?.dataset.currentSortBy || '';

    if (!FacetFiltersForm.isLocalizedTitleSort(window.location.search.slice(1), currentSortBy)) {
      FacetFiltersForm.setLocalizedTitleSortReady();
      return;
    }

    const section = FacetFiltersForm.getSections()[0];
    const searchParams = window.location.search.slice(1);
    const url = `${window.location.pathname}?section_id=${section.section}${searchParams ? `&${searchParams}` : ''}`;
    const filterDataUrl = (element) => element.url === url;

    if (FacetFiltersForm.filterData.some(filterDataUrl)) {
      await FacetFiltersForm.renderSectionFromCache(filterDataUrl, null);
      return;
    }

    await FacetFiltersForm.renderSectionFromFetch(url, null);
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    return new URLSearchParams(formData).toString();
  }

  createSearchParamsFromForms(forms) {
    const searchParams = new URLSearchParams();

    forms.forEach((form) => {
      const formData = new FormData(form);

      for (const [key, value] of formData.entries()) {
        searchParams.append(key, value);
      }
    });

    return searchParams.toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const sortFilterForms = document.querySelectorAll('facet-filters-form form');
    if (event.srcElement.className == 'mobile-facets__checkbox') {
      const searchParams = this.createSearchParams(event.target.closest('form'));
      this.onSubmitForm(searchParams, event);
    } else {
      const forms = [];
      const isMobile = event.target.closest('form').id === 'FacetFiltersFormMobile';

      sortFilterForms.forEach((form) => {
        if (!isMobile) {
          if (form.id === 'FacetSortForm' || form.id === 'FacetFiltersForm' || form.id === 'FacetSortDrawerForm') {
            const noJsElements = document.querySelectorAll('.no-js-list');
            noJsElements.forEach((el) => el.remove());
            forms.push(form);
          }
        } else if (form.id === 'FacetFiltersFormMobile') {
          forms.push(form);
        }
      });
      this.onSubmitForm(this.createSearchParamsFromForms(forms), event);
    }
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url =
      event.currentTarget.href.indexOf('?') == -1
        ? ''
        : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    FacetFiltersForm.renderPage(url);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.localizedTitleSortData = {};
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();
FacetFiltersForm.initializeLocalizedTitleSort();

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll('input').forEach((element) =>
      element.addEventListener('change', this.onRangeChange.bind(this))
    );
    this.setMinAndMaxValues();
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll('input');
    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (maxInput.value) minInput.setAttribute('max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('min', 0);
    if (maxInput.value === '') minInput.setAttribute('max', maxInput.getAttribute('max'));
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute('min'));
    const max = Number(input.getAttribute('max'));

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }
}

customElements.define('price-range', PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    form.onActiveFilterClick(event);
  }
}

customElements.define('facet-remove', FacetRemove);
